import { DatePicker, LocalizationProvider, DatePickerProps } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useController, useFormContext } from 'react-hook-form';
import { z } from 'zod';

export interface IDatePickerInputProps extends DatePickerProps<Date> {
	label: string;
	name: string;
	helperText?: string;
	required?: boolean;
	desktopMode?: boolean;
	autoComplete?: string;
	minDate?: Date;
	maxDate?: Date;
	slotProps?: DatePickerProps<Date>['slotProps'];
	disabled?: boolean;
}

type ValueOf<T> = T[keyof T];

export const datePickerInputSchema = ((options) => {
	return {
		options,
		create: (fieldName: string, validation: ValueOf<typeof options>) => {
			return z.object({ [fieldName]: validation });
		},
	};
})({
	// validations:
	default: z.date({ invalid_type_error: 'Required' }),
	notRequired: z.date(),
});

export default function DatePickerInput({
	label,
	name,
	helperText,
	required = true,
	desktopMode = false,
	autoComplete = 'off',
	minDate,
	maxDate,
	slotProps,
	disabled = false,
	...props
}: IDatePickerInputProps) {
	const { control } = useFormContext();

	const {
		field,
		formState: { errors },
	} = useController({ name, control });

	const error = errors[name];

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<DatePicker
				{...field}
				label={label}
				slotProps={{
					textField: {
						...field,
						error: !!error,
						helperText: <>{error?.message || helperText}</>,
						required,
						fullWidth: true,
						autoComplete,
					},
					...slotProps,
				}}
				minDate={minDate}
				maxDate={maxDate}
				desktopModeMediaQuery={desktopMode ? '@media (min-width: 0)' : undefined}
				disabled={disabled}
				{...props}
			/>
		</LocalizationProvider>
	);
}
